import { useEffect, useState } from "react";
import axios from "axios";
import { API, authHeaders } from "@/constants/roles";
import { X, Loader2, Download } from "lucide-react";

/**
 * Read-only proof-of-payment viewer. Fetches the file as an authenticated blob
 * (proof URLs are never public), then renders images inline and PDFs in a frame.
 * Used by the owner payment modal and by workers viewing their OWN payment proof.
 */
export const ProofModal = ({ payment, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  const isPdf = (payment.proof_content_type || "").includes("pdf");

  useEffect(() => {
    let url;
    axios
      .get(`${API}/payments/${payment.id}/proof`, { headers: authHeaders(), responseType: "blob" })
      .then((r) => {
        url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      })
      .catch(() => setError(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [payment.id]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div data-testid="proof-modal" className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b border-[var(--pv-border)] px-5 py-3.5">
          <p className="truncate text-sm font-bold text-[var(--pv-text)]">Proof of payment — {payment.proof_filename || "receipt"}</p>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a data-testid="proof-download" href={blobUrl} download={payment.proof_filename || "proof"} className="rounded-full p-2 text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]" aria-label="Download">
                <Download className="h-4 w-4" />
              </a>
            )}
            <button data-testid="proof-modal-close" onClick={onClose} className="rounded-full p-2 text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {error ? (
            <p data-testid="proof-error" className="py-16 text-center text-sm text-[var(--pv-text-4)]">Could not load the proof file.</p>
          ) : !blobUrl ? (
            <p className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[var(--pv-text-4)]" /></p>
          ) : isPdf ? (
            <iframe data-testid="proof-pdf" src={blobUrl} title="Proof of payment" className="h-[70vh] w-full rounded-2xl border border-[var(--pv-border)] bg-white" />
          ) : (
            <img data-testid="proof-image" src={blobUrl} alt="Proof of payment" className="mx-auto max-h-[70vh] rounded-2xl" />
          )}
        </div>
      </div>
    </div>
  );
};
